import { IDashboard, IWidget } from '@/pages/Dashboards/dashboards.types.ts'
import React, { ComponentType, useId, useMemo, useRef, useState } from 'react'
import LineChart from '@/pages/Dashboards/Charts/LineChart'
import SVGChart from '@/pages/Dashboards/Charts/SVGChart'
import { CardMoveType } from '@/pages/Dashboards/services/autoalign/autoalign.types.ts'
import usePopup from '@/shared/hooks/usePopup.ts'
import usePlayer from '@/pages/Dashboards/services/player/usePlayer.ts'
import dashboardsUtils from '@/pages/Dashboards/dashboards.utils.ts'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import dashboardsApi from '@/pages/Dashboards/dashboards.api.ts'
import dashboardsEvents from '@/pages/Dashboards/events/dashboards.events.ts'
import css from '@/pages/Dashboards/pages/DashboardScreen/WidgetCard/widgetCard.module.css'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import DropdownUnderThreeDots from '@/shared/Dropdowns/DropdownUnderThreeDots'
import DropdownListItemSettings from '@/shared/Dropdowns/DropdownListItem/DropdownListItemSettings'
import DropdownListItemDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import ChartErrorBoundary from '@/pages/Dashboards/Charts/lib/ChartErrorBoundary'
import CardPositionControls from '@/pages/Dashboards/pages/DashboardScreen/WidgetCard/CardPositionControls'
import UpdateWidgetSidePage from '@/pages/Dashboards/components/UpdateWidgetSidePage'
import useLastValuesRef, { IUseLastValuesProps } from '@/services/data/hooks/useLastValuesRef.ts'
import DragIcon from '@/assets/icons/drag.svg?react'
import TextChart from '@/pages/Dashboards/Charts/TextChart'
import TableChart from '@/pages/Dashboards/Charts/TableChart'

const components: { [type in IWidget['type']]?: ComponentType<any> } = {
  linear: LineChart,
  svg: SVGChart,
  text: TextChart,
  table: TableChart,
}

interface IProps {
  dashboardId: IDashboard['id']
  component: IWidget
  onMove: (position: IWidget, type: CardMoveType) => void
  onMoveFinish: () => void
  viewport: number
}

const LastValuesWidgetCard = (props: IProps) => {
  const dataSubId = useId()
  const [lastResize, setResize] = useState(0)
  const [updateCounter, setUpdateCounter] = useState(0)

  const [updateModalOpen, setUpdateModalOpen] = useState(false)

  const cardRef = useRef<HTMLDivElement>()
  const menuRef = useRef<HTMLDivElement>()
  const { open: menuOpen, setOpen: setMenuOpen } = usePopup(menuRef)

  const player = usePlayer()

  const widgetDataConfig: IUseLastValuesProps = useMemo(() => {
    const devicesAndParameters = dashboardsUtils.getWidgetDataConfig(props.component)

    return {
      tsFrom: player.tsFrom,
      live: player.live,
      id: dataSubId,
      onNewValueReceived: () => setUpdateCounter((prev) => prev + 1),
      ...devicesAndParameters,
    }
  }, [props.component, player, dataSubId])

  const handleDelete = () => {
    confirmService.requestConfirm({
      description: `Подтвердите удаление виджета"`,
      buttonType: 'primary',
      onConfirm: () =>
        dashboardsApi
          .deleteWidget(props.component.id)
          .then((r) => r.status === 'success' && dashboardsEvents.widgetDeleted(r.body.id)),
    })
  }

  const dataRef = useLastValuesRef(widgetDataConfig)

  const Component: any = components[props.component.type]

  const renderContent = () => {
    if (!dataRef.ref || dataRef.loading) {
      return (
        <div className={css.loaderWrapper}>
          <ThreeDotsLoader />
        </div>
      )
    }

    return (
      <div style={{ flex: 1, minHeight: 0 }}>
        <Component
          updateCounter={updateCounter}
          widget={props.component}
          dashboard={props.component.dashboardId}
          dataRef={dataRef.ref}
        />
      </div>
    )
  }

  return (
    <div
      ref={cardRef}
      className={css.wrapper}
      onContextMenu={(ev) => {
        ev.stopPropagation()
        ev.preventDefault()

        setMenuOpen(true)
      }}
    >
      <div className="flex-space-between">
        <div className="inline-flex-gap gap4 center">
          <DragIcon style={{ cursor: 'grab', width: '32px', height: '32px', padding: '8px' }} />
          <p className="text-nm">{(props.component.configuration.settings as any).title}</p>
        </div>
        <DropdownUnderThreeDots open={menuOpen} setOpen={setMenuOpen} ref={menuRef} position="left">
          <DropdownListItemSettings text="Настройки" onClick={() => setUpdateModalOpen(true)} />
          <DropdownListItemDelete text="Удалить" onClick={handleDelete} />
        </DropdownUnderThreeDots>
      </div>
      <ChartErrorBoundary>{renderContent()}</ChartErrorBoundary>
      <CardPositionControls {...props} cardRef={cardRef} onResize={() => setResize(lastResize + 1)} />
      <UpdateWidgetSidePage widget={props.component} open={updateModalOpen} onClose={() => setUpdateModalOpen(false)} />
    </div>
  )
}

export default React.memo(LastValuesWidgetCard)
